import DashboardNavigation from "@/components/nav-main";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { navigation, routes } from "@/config/navigation";
import { useNavigate } from "@tanstack/react-router";
import { useAtom } from "jotai";
import {
  ChevronDown,
  ChevronRight,
  Gift,
  LogOut,
  Settings,
  UserCog,
  Wallet,
} from "lucide-react";
import type React from "react";
import { Suspense, useMemo } from "react";
import { clearAuthAtom, useUserInfo } from "../../../api/custom";
import { CommandPalette } from "./CommandPalette";

interface AppLayoutProps {
  children: React.ReactNode;
  title?: string;
}

export function AppLayout({ children, title }: AppLayoutProps) {
  const [, clearAuth] = useAtom(clearAuthAtom);
  const navigate = useNavigate();
  const userInfoQuery = useUserInfo();

  const handleLogout = () => {
    clearAuth();
    navigate({ to: "/login" });
  };

  const userInfo = userInfoQuery.data?.userInfo;

  const filteredRoutes = useMemo(() => {
    const userRole = userInfo?.role;
    return routes.filter((route) => {
      if (route.requiredRole) {
        return userRole === route.requiredRole;
      }
      return true;
    });
  }, [userInfo?.role]);

  const filteredNavigation = useMemo(() => {
    const userRole = userInfo?.role;
    return navigation
      .filter((section) => {
        if (section.requiredRole) {
          return userRole === section.requiredRole;
        }
        return true;
      })
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          if (item.requiredRole) {
            return userRole === item.requiredRole;
          }
          return true;
        }),
      }));
  }, [userInfo?.role]);

  return (
    <SidebarProvider>
      <div className="relative flex h-screen w-full">
        <Sidebar variant="floating" collapsible="icon" className="w-72">
          <SidebarHeader className="flex p-4 py-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-3">
            <a
              href="/"
              className="flex items-center gap-3 group-data-[collapsible=icon]:gap-0"
            >
              <img
                src="/main-logo.jpeg"
                alt="Logo"
                className="h-10 w-10 rounded-md group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8"
              />
              <span className="font-semibold text-base group-data-[collapsible=icon]:hidden">
                Munchi Portal
              </span>
            </a>
          </SidebarHeader>
          <SidebarContent className="gap-4 px-3 py-4 pt-1 group-data-[collapsible=icon]:px-1">
            <DashboardNavigation routes={filteredRoutes} />
          </SidebarContent>
          <div className="mt-auto p-3 space-y-3 group-data-[collapsible=icon]:p-2 group-data-[collapsible=icon]:space-y-2">
            <a
              href="https://lnk.gomunchi.com/YFvV"
              target="_blank"
              rel="noopener noreferrer"
              className="block group-data-[collapsible=icon]:hidden"
            >
              <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20 hover:shadow-lg transition-all duration-300 cursor-pointer">
                <CardContent className="p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <Gift className="h-5 w-5 text-primary shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm truncate">
                          Refer & Earn
                        </h3>
                        <p className="text-xs text-muted-foreground truncate">
                          Invite friends
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                </CardContent>
              </Card>
            </a>

            <a
              href="https://www.qred.fi/partneri/munchi"
              target="_blank"
              rel="noopener noreferrer"
              className="block group-data-[collapsible=icon]:hidden"
            >
              <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/5 border-green-500/20 hover:shadow-lg transition-all duration-300 cursor-pointer">
                <CardContent className="p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <Wallet className="h-5 w-5 text-green-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm truncate">
                          Apply for Loan
                        </h3>
                        <p className="text-xs text-muted-foreground truncate">
                          Quick approval
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                </CardContent>
              </Card>
            </a>

            <a
              href="https://lnk.gomunchi.com/YFvV"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden group-data-[collapsible=icon]:flex items-center justify-center"
            >
              <div className="p-3 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors">
                <Gift className="h-6 w-6 text-primary" />
              </div>
            </a>

            <a
              href="https://www.qred.fi/partneri/munchi"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden group-data-[collapsible=icon]:flex items-center justify-center"
            >
              <div className="p-3 rounded-lg bg-green-500/10 hover:bg-green-500/20 transition-colors">
                <Wallet className="h-6 w-6 text-green-600" />
              </div>
            </a>
          </div>
        </Sidebar>
        <SidebarInset className="flex flex-col">
          <header className="flex h-20 shrink-0 items-center gap-3 border-b px-6">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-8" />
            <div className="flex items-center gap-4 flex-1">
              {title && <h1 className="text-2xl font-semibold">{title}</h1>}
            </div>
            <div className="flex items-center gap-3">
              <CommandPalette navigationItems={filteredNavigation} />
              <Separator orientation="vertical" className="h-8" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground text-base font-medium">
                        {userInfo?.name?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <ChevronDown className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {userInfo?.name || "Loading..."}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {userInfo?.email || "Loading..."}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="opacity-50 cursor-not-allowed">
                    <UserCog className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="opacity-50 cursor-not-allowed">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          <main className="flex flex-1 flex-col gap-6 overflow-auto">
            <Suspense fallback={null}>
              {children}
            </Suspense>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
